import React, { useState } from 'react';
import AnimatedSection from '../components/AnimatedSection';
import { trainingPrograms } from '../data/content';

const Training = () => {
  // WhatsApp number - Replace with your actual WhatsApp number
  const whatsappNumber = "919986352227";
  const [hoveredProgram, setHoveredProgram] = useState(null);
  const [selectedProgram, setSelectedProgram] = useState(null);
  const [showModal, setShowModal] = useState(false);

  // Function to generate WhatsApp link with detailed message
  const getWhatsAppLink = (programTitle, programDetails) => {
    const message = `Hi! I'm interested in the "${programTitle}" training program.\n\nProgram Details:\n${programDetails}\n\nCould you please provide more information about:\n- Course Curriculum\n- Fee Structure\n- Schedule & Duration\n- Certification\n- Placement Assistance\n\nThank you!`;
    const encodedMessage = encodeURIComponent(message);
    return `https://wa.me/${whatsappNumber}?text=${encodedMessage}`;
  };

  // Open modal with program details
  const openModal = (program) => {
    setSelectedProgram(program);
    setShowModal(true);
    document.body.style.overflow = 'hidden';
  };

  const closeModal = () => {
    setShowModal(false);
    setSelectedProgram(null);
    document.body.style.overflow = 'auto';
  };

  return (
    <>
      <section className="training-section">
        {/* Animated Background */}
        <div className="animated-bg">
          <div className="gradient-orb-1"></div>
          <div className="gradient-orb-2"></div>
          <div className="gradient-orb-3"></div>
          <div className="floating-shapes">
            {[...Array(25)].map((_, i) => (
              <div key={i} className="shape" style={{
                left: `${Math.random() * 100}%`,
                animationDelay: `${Math.random() * 15}s`,
                animationDuration: `${12 + Math.random() * 18}s`,
                width: `${15 + Math.random() * 50}px`,
                height: `${15 + Math.random() * 50}px`,
                opacity: 0.03 + Math.random() * 0.05
              }}></div>
            ))}
          </div>
          <div className="grid-pattern"></div>
        </div>

        <div className="container">
          <AnimatedSection animation="fade-up">
            <div className="section-header">
              <span className="section-badge">
                <i className="fas fa-chalkboard-user"></i> Career Building Programs
              </span>
              <h2 className="section-title">
                Training Programs
              </h2>
              <p className="section-description">
                Choose from our industry-focused training programs designed for career growth and skill development
              </p>
            </div>
          </AnimatedSection>
          
          <div className="programs-grid">
            {trainingPrograms.map((program, idx) => (
              <AnimatedSection key={idx} animation="scale-up" delay={idx * 80}>
                <div 
                  className="program-card"
                  onMouseEnter={() => setHoveredProgram(idx)}
                  onMouseLeave={() => setHoveredProgram(null)}
                >
                  {/* Card Glow Effect */}
                  <div className="card-glow" style={{ opacity: hoveredProgram === idx ? 1 : 0 }}></div>
                  
                  {/* Icon with Animation */}
                  <div className="icon-container">
                    <div className="icon-bg"></div>
                    <div className="icon-wrapper">
                      <i className={program.icon}></i>
                    </div>
                    {program.badge && (
                      <div className="icon-badge">
                        <i className="fas fa-star"></i>
                      </div>
                    )}
                  </div>
                  
                  <h3>{program.title}</h3>
                  <p className="program-description">{program.description}</p>
                  
                  {/* Program Highlights */}
                  {program.highlights && (
                    <div className="program-highlights">
                      {program.highlights.slice(0, 3).map((highlight, i) => (
                        <div key={i} className="highlight-chip">
                          <i className="fas fa-check-circle"></i>
                          <span>{highlight}</span>
                        </div>
                      ))}
                    </div>
                  )}
                  
                  {/* Duration and Level Tags */}
                  <div className="program-meta">
                    {program.duration && (
                      <span className="meta-tag">
                        <i className="fas fa-clock"></i> {program.duration}
                      </span>
                    )}
                    {program.level && (
                      <span className="meta-tag level">
                        <i className="fas fa-chart-line"></i> {program.level}
                      </span>
                    )}
                  </div>
                  
                  {/* Action Buttons */}
                  <div className="action-buttons">
                    <button 
                      className="details-btn"
                      onClick={() => openModal(program)}
                    >
                      <i className="fas fa-info-circle"></i>
                      <span>View Details</span>
                    </button>
                    <a 
                      href={getWhatsAppLink(program.title, program.fullDescription || program.description)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="enquiry-btn"
                    >
                      <i className="fab fa-whatsapp"></i>
                      <span>Enquire Now</span>
                    </a>
                  </div>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* Program Details Modal */}
      {showModal && selectedProgram && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal-container" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close" onClick={closeModal}>
              <i className="fas fa-times"></i>
            </button>
            
            <div className="modal-content">
              <div className="modal-header">
                <div className="modal-icon">
                  <i className={selectedProgram.icon}></i>
                </div>
                <h2>{selectedProgram.title}</h2>
                <div className="modal-tags">
                  {selectedProgram.duration && (
                    <span className="modal-tag duration">
                      <i className="fas fa-clock"></i> {selectedProgram.duration}
                    </span>
                  )}
                  {selectedProgram.level && (
                    <span className="modal-tag level">
                      <i className="fas fa-signal"></i> {selectedProgram.level}
                    </span>
                  )}
                </div>
              </div>
              
              <div className="modal-body">
                <div className="description-section">
                  <h3>
                    <i className="fas fa-info-circle"></i>
                    Program Overview
                  </h3>
                  <p>{selectedProgram.fullDescription || selectedProgram.description}</p>
                </div>
                
                {selectedProgram.curriculum && (
                  <div className="curriculum-section">
                    <h3>
                      <i className="fas fa-book-open"></i>
                      What You'll Learn
                    </h3>
                    <div className="curriculum-grid">
                      {selectedProgram.curriculum.map((item, i) => (
                        <div key={i} className="curriculum-item">
                          <i className="fas fa-check"></i>
                          <span>{item}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                
                {selectedProgram.careerPaths && (
                  <div className="career-section">
                    <h3>
                      <i className="fas fa-rocket"></i>
                      Career Opportunities
                    </h3>
                    <div className="career-paths">
                      {selectedProgram.careerPaths.map((path, i) => (
                        <span key={i} className="career-path">{path}</span>
                      ))}
                    </div>
                  </div>
                )}
                
                {selectedProgram.prerequisites && (
                  <div className="prerequisites-section">
                    <h3>
                      <i className="fas fa-graduation-cap"></i>
                      Prerequisites
                    </h3>
                    <ul>
                      {selectedProgram.prerequisites.map((pre, i) => (
                        <li key={i}>
                          <i className="fas fa-arrow-right"></i>
                          <span>{pre}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                
                <div className="modal-actions">
                  <a 
                    href={getWhatsAppLink(selectedProgram.title, selectedProgram.fullDescription || selectedProgram.description)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="modal-whatsapp-btn"
                  >
                    <i className="fab fa-whatsapp"></i>
                    <span>Enquire Now on WhatsApp</span>
                    <i className="fas fa-arrow-right"></i>
                  </a>
                  <button className="modal-close-btn" onClick={closeModal}>
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      <style jsx>{`
        /* Main Section */
        .training-section {
          padding: 5rem 0;
          position: relative;
          overflow: hidden;
          min-height: 100vh;
        }
        
        /* Animated Background */
        .animated-bg {
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          z-index: 0;
          overflow: hidden;
        }
        
        .gradient-orb-1,
        .gradient-orb-2,
        .gradient-orb-3 {
          position: absolute;
          border-radius: 50%;
          filter: blur(100px);
          animation: floatOrb 20s ease-in-out infinite;
        }
        
        .gradient-orb-1 {
          width: 500px;
          height: 500px;
          background: radial-gradient(circle, rgba(59, 130, 246, 0.3), rgba(139, 92, 246, 0.2));
          top: -250px;
          left: -250px;
          animation-delay: 0s;
        }
        
        .gradient-orb-2 {
          width: 600px;
          height: 600px;
          background: radial-gradient(circle, rgba(139, 92, 246, 0.25), rgba(236, 72, 153, 0.15));
          bottom: -300px;
          right: -300px;
          animation-delay: -5s;
        }
        
        .gradient-orb-3 {
          width: 400px;
          height: 400px;
          background: radial-gradient(circle, rgba(6, 182, 212, 0.2), rgba(59, 130, 246, 0.15));
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          animation-delay: -10s;
        }
        
        @keyframes floatOrb {
          0%, 100% {
            transform: translate(0, 0) scale(1);
          }
          33% {
            transform: translate(50px, -50px) scale(1.1);
          }
          66% {
            transform: translate(-50px, 50px) scale(0.9);
          }
        }
        
        .floating-shapes {
          position: absolute;
          width: 100%;
          height: 100%;
        }
        
        .shape {
          position: absolute;
          background: linear-gradient(135deg, #3b82f6, #8b5cf6);
          border-radius: 50%;
          animation: floatShape linear infinite;
        }
        
        @keyframes floatShape {
          0% {
            transform: translateY(100vh) rotate(0deg);
          }
          100% {
            transform: translateY(-100vh) rotate(360deg);
          }
        }
        
        .grid-pattern {
          position: absolute;
          width: 100%;
          height: 100%;
          background-image: 
            linear-gradient(rgba(59, 130, 246, 0.03) 1px, transparent 1px),
            linear-gradient(90deg, rgba(59, 130, 246, 0.03) 1px, transparent 1px);
          background-size: 60px 60px;
          animation: gridMove 30s linear infinite;
        }
        
        @keyframes gridMove {
          0% { transform: translate(0, 0); }
          100% { transform: translate(60px, 60px); }
        }
        
        /* Section Header */
        .section-header {
          text-align: center;
          margin-bottom: 3rem;
          position: relative;
          z-index: 2;
        }
        
        .section-badge {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          background: linear-gradient(135deg, rgba(59, 130, 246, 0.2), rgba(139, 92, 246, 0.2));
          padding: 0.5rem 1rem;
          border-radius: 50px;
          font-size: 0.85rem;
          font-weight: 600;
          color: #3b82f6;
          margin-bottom: 1rem;
          backdrop-filter: blur(10px);
        }
        
        .section-title {
          font-size: 2.8rem;
          font-weight: 800;
          margin-bottom: 1rem;
          background: linear-gradient(135deg, #1e293b, #3b82f6);
          -webkit-background-clip: text;
          background-clip: text;
          color: transparent;
        }
        
        .section-description {
          color: #64748b;
          max-width: 600px;
          margin: 0 auto;
          font-size: 1.1rem;
        }
        
        /* Programs Grid */
        .programs-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(350px, 1fr));
          gap: 2rem;
          position: relative;
          z-index: 2;
        }
        
        /* Program Card */
        .program-card {
          background: rgba(255, 255, 255, 0.95);
          backdrop-filter: blur(10px);
          border-radius: 28px;
          padding: 2rem;
          transition: all 0.4s cubic-bezier(0.2, 0.9, 0.4, 1.1);
          border: 1px solid rgba(59, 130, 246, 0.1);
          position: relative;
          overflow: hidden;
          height: 100%;
          display: flex;
          flex-direction: column;
        }
        
        .card-glow {
          position: absolute;
          inset: 0;
          background: radial-gradient(circle at 50% 0%, rgba(59, 130, 246, 0.15), transparent 70%);
          opacity: 0;
          transition: opacity 0.4s ease;
          pointer-events: none;
        }
        
        .program-card:hover {
          transform: translateY(-12px);
          box-shadow: 0 25px 45px rgba(0, 0, 0, 0.12);
          border-color: rgba(59, 130, 246, 0.3);
        }
        
        /* Icon Container */
        .icon-container {
          position: relative;
          width: 80px;
          height: 80px;
          margin-bottom: 1.5rem;
        }
        
        .icon-bg {
          position: absolute;
          inset: 0;
          background: linear-gradient(135deg, #3b82f6, #8b5cf6);
          border-radius: 24px;
          opacity: 0.1;
          transition: all 0.3s ease;
        }
        
        .program-card:hover .icon-bg {
          opacity: 0.2;
          transform: scale(1.1);
        }
        
        .icon-wrapper {
          position: relative;
          width: 80px;
          height: 80px;
          background: linear-gradient(135deg, rgba(59, 130, 246, 0.1), rgba(139, 92, 246, 0.1));
          border-radius: 24px;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: all 0.3s ease;
          z-index: 1;
        }
        
        .program-card:hover .icon-wrapper {
          background: linear-gradient(135deg, rgba(59, 130, 246, 0.2), rgba(139, 92, 246, 0.2));
          transform: scale(1.05);
        }
        
        .icon-wrapper i {
          font-size: 2.5rem;
          color: #3b82f6;
          transition: transform 0.3s ease;
        }
        
        .program-card:hover .icon-wrapper i {
          transform: scale(1.1);
        }
        
        .icon-badge {
          position: absolute;
          top: -8px;
          right: -8px;
          width: 28px;
          height: 28px;
          background: linear-gradient(135deg, #f59e0b, #ef4444);
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 2;
          animation: pulse 2s ease-in-out infinite;
        }
        
        .icon-badge i {
          font-size: 0.7rem;
          color: white;
        }
        
        @keyframes pulse {
          0%, 100% { transform: scale(1); }
          50% { transform: scale(1.1); }
        }
        
        /* Title */
        h3 {
          font-size: 1.4rem;
          font-weight: 700;
          margin-bottom: 0.75rem;
          color: #1e293b;
          transition: all 0.3s ease;
        }
        
        .program-card:hover h3 {
          background: linear-gradient(135deg, #3b82f6, #8b5cf6);
          -webkit-background-clip: text;
          background-clip: text;
          color: transparent;
        }
        
        /* Description */
        .program-description {
          color: #64748b;
          line-height: 1.6;
          margin-bottom: 1rem;
          font-size: 0.9rem;
        }
        
        /* Program Highlights */
        .program-highlights {
          display: flex;
          flex-wrap: wrap;
          gap: 0.5rem;
          margin: 1rem 0;
        }
        
        .highlight-chip {
          display: flex;
          align-items: center;
          gap: 6px;
          background: linear-gradient(135deg, rgba(59, 130, 246, 0.1), rgba(139, 92, 246, 0.1));
          padding: 4px 10px;
          border-radius: 50px;
          font-size: 0.7rem;
          font-weight: 500;
          color: #3b82f6;
        }
        
        .highlight-chip i {
          font-size: 0.65rem;
        }
        
        /* Program Meta */
        .program-meta {
          display: flex;
          gap: 0.8rem;
          margin: 0.5rem 0 1rem;
          flex-wrap: wrap;
        }
        
        .meta-tag {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          font-size: 0.7rem;
          padding: 4px 10px;
          background: #f1f5f9;
          border-radius: 50px;
          color: #475569;
        }
        
        .meta-tag.level {
          background: linear-gradient(135deg, rgba(59, 130, 246, 0.1), rgba(139, 92, 246, 0.1));
          color: #3b82f6;
        }
        
        .meta-tag i {
          font-size: 0.65rem;
        }
        
        /* Action Buttons */
        .action-buttons {
          display: flex;
          gap: 1rem;
          margin-top: auto;
        }
        
        .details-btn {
          flex: 1;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          background: transparent;
          border: 1px solid #e2e8f0;
          padding: 10px;
          border-radius: 50px;
          font-weight: 600;
          font-size: 0.85rem;
          cursor: pointer;
          transition: all 0.3s ease;
          color: #1e293b;
        }
        
        .details-btn:hover {
          background: #f8fafc;
          border-color: #3b82f6;
          transform: translateY(-2px);
        }
        
        .enquiry-btn {
          flex: 1;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          background: linear-gradient(135deg, #25D366, #128C7E);
          color: white;
          padding: 10px;
          border-radius: 50px;
          text-decoration: none;
          font-weight: 600;
          font-size: 0.85rem;
          transition: all 0.3s ease;
        }
        
        .enquiry-btn:hover {
          transform: translateY(-2px);
          box-shadow: 0 5px 15px rgba(37, 211, 102, 0.3);
        }
        
        /* Modal Styles */
        .modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.85);
          backdrop-filter: blur(12px);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 3000;
          animation: fadeIn 0.3s ease;
        }
        
        .modal-container {
          position: relative;
          max-width: 800px;
          width: 90%;
          max-height: 85vh;
          background: white;
          border-radius: 32px;
          overflow: hidden;
          animation: slideUp 0.4s cubic-bezier(0.2, 0.9, 0.4, 1.1);
        }
        
        .modal-close {
          position: absolute;
          top: 20px;
          right: 20px;
          width: 40px;
          height: 40px;
          border-radius: 50%;
          background: rgba(0, 0, 0, 0.5);
          backdrop-filter: blur(4px);
          border: none;
          color: white;
          font-size: 1.2rem;
          cursor: pointer;
          z-index: 10;
          transition: all 0.3s ease;
          display: flex;
          align-items: center;
          justify-content: center;
        }
        
        .modal-close:hover {
          background: #ef4444;
          transform: rotate(90deg);
        }
        
        .modal-content {
          max-height: 85vh;
          overflow-y: auto;
        }
        
        .modal-header {
          background: linear-gradient(135deg, #3b82f6, #8b5cf6);
          padding: 2rem;
          text-align: center;
          color: white;
        }
        
        .modal-icon {
          width: 80px;
          height: 80px;
          background: rgba(255, 255, 255, 0.2);
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          margin: 0 auto 1rem;
        }
        
        .modal-icon i {
          font-size: 2.5rem;
        }
        
        .modal-header h2 {
          font-size: 1.8rem;
          margin-bottom: 1rem;
        }
        
        .modal-tags {
          display: flex;
          justify-content: center;
          gap: 1rem;
        }
        
        .modal-tag {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          padding: 4px 12px;
          background: rgba(255, 255, 255, 0.2);
          border-radius: 50px;
          font-size: 0.8rem;
        }
        
        .modal-body {
          padding: 2rem;
        }
        
        .modal-body h3 {
          font-size: 1.2rem;
          margin-bottom: 1rem;
          display: flex;
          align-items: center;
          gap: 8px;
          color: #1e293b;
        }
        
        .description-section p {
          color: #475569;
          line-height: 1.6;
          margin-bottom: 1.5rem;
        }
        
        .curriculum-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
          gap: 0.8rem;
          margin-bottom: 1.5rem;
        }
        
        .curriculum-item {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 8px;
          background: #f8fafc;
          border-radius: 8px;
        }
        
        .curriculum-item i {
          color: #10b981;
        }
        
        .career-paths {
          display: flex;
          flex-wrap: wrap;
          gap: 0.8rem;
          margin-bottom: 1.5rem;
        }
        
        .career-path {
          background: linear-gradient(135deg, rgba(59, 130, 246, 0.1), rgba(139, 92, 246, 0.1));
          padding: 6px 14px;
          border-radius: 50px;
          font-size: 0.85rem;
          color: #3b82f6;
        }
        
        .prerequisites-section ul {
          list-style: none;
          padding: 0;
        }
        
        .prerequisites-section li {
          display: flex;
          align-items: center;
          gap: 10px;
          margin-bottom: 0.5rem;
          color: #475569;
        }
        
        .prerequisites-section li i {
          color: #3b82f6;
        }
        
        .modal-actions {
          display: flex;
          gap: 1rem;
          margin-top: 2rem;
          padding-top: 1rem;
          border-top: 1px solid #e2e8f0;
        }
        
        .modal-whatsapp-btn {
          flex: 2;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 10px;
          background: linear-gradient(135deg, #25D366, #128C7E);
          color: white;
          padding: 12px;
          border-radius: 50px;
          text-decoration: none;
          font-weight: 600;
          transition: all 0.3s ease;
        }
        
        .modal-whatsapp-btn:hover {
          transform: translateY(-2px);
          gap: 15px;
        }
        
        .modal-close-btn {
          flex: 1;
          padding: 12px;
          background: #e2e8f0;
          border: none;
          border-radius: 50px;
          cursor: pointer;
          font-weight: 600;
          transition: all 0.3s ease;
        }
        
        .modal-close-btn:hover {
          background: #cbd5e1;
        }
        
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        
        @keyframes slideUp {
          from {
            transform: translateY(50px);
            opacity: 0;
          }
          to {
            transform: translateY(0);
            opacity: 1;
          }
        }
        
        /* Responsive */
        @media (max-width: 768px) {
          .section-title {
            font-size: 2rem;
          }
          
          .programs-grid {
            grid-template-columns: 1fr;
          }
          
          .modal-header h2 {
            font-size: 1.4rem;
          }
          
          .modal-body {
            padding: 1.5rem;
          }
          
          .action-buttons {
            flex-direction: column;
          }
          
          .modal-actions {
            flex-direction: column;
          }
        }
        
        /* Dark Mode */
        @media (prefers-color-scheme: dark) {
          .program-card {
            background: rgba(30, 41, 59, 0.95);
          }
          
          h3 {
            color: #f1f5f9;
          }
          
          .program-description {
            color: #94a3b8;
          }
          
          .meta-tag {
            background: #334155;
            color: #cbd5e1;
          }
          
          .details-btn {
            border-color: #334155;
            color: #cbd5e1;
          }
          
          .details-btn:hover {
            background: #334155;
          }
          
          .modal-container {
            background: #1e293b;
          }
          
          .modal-body h3 {
            color: #f1f5f9;
          }
          
          .description-section p {
            color: #94a3b8;
          }
          
          .curriculum-item {
            background: #0f172a;
            color: #cbd5e1;
          }
          
          .prerequisites-section li {
            color: #cbd5e1;
          }
        }
      `}</style>
    </>
  );
};

export default Training;